/**
 * Provide user authentication flow management controllers
 *
 * @module
 */

import {popup} from '@acng/frontend-discovery';
import {noop} from '@acng/frontend-bounty/std/value.js';
import {remove} from '@acng/frontend-bounty/object.js';
import {createPromise} from '@acng/frontend-bounty/std/control.js';
import {GlobalWatch} from '@acng/frontend-relativity';
import {location, service, click} from 'nova';

import {ctxSearchParams} from '../../core/context/search-params.js';
import {coreFeature} from '../../core/config/feature.js';
import {post} from '../../core/service/backend.js';
import {t} from '../../locale/config/translate.js';
import {UserLogin} from './login.js';
import {UserSignup} from './signup.js';
import {GUEST, KICK, USER, LOGOUT} from '../model/dictionary.js';
import {user} from '../user.js';
import {disconnectLocation} from 'acng/core/service/location.js';
import {NotificationManager} from '../notification-manager.js';
import {make} from '../../core/service/env.js';

/**
 * Auth Service
 *
 * Provide user flow controller.
 *
 * - Signup
 * - Login
 * - Logout
 * - Request confirm mail again
 * - Request password restore mail
 */
@service
class Auth {
  /**
   * @example
   *
   * ```html
   * <form data-auth="signup">
   *   <input name="nickname" />
   *   <input name="email" />
   *   <input type="submit" />
   * </form>
   * ```
   */
  signup = make(UserSignup);
  login = make(UserLogin);

  /**
   * Only authenticated user
   *
   * @example
   *
   * ```html
   * <p data-auth="user">Hello ${user.name}!</p>
   * ```
   *
   * @see {@link USER}
   */
  get user() {
    return user.guest ? null : user;
  }

  /**
   * Only unauthenticated user
   *
   * @example
   *
   * ```html
   * <p data-auth="guest">Please log in!</p>
   * ```
   *
   * @see {@link GUEST}
   */
  get guest() {
    return user.guest ? user : null;
  }

  /**
   * 1. Ask the user and only continue if they want to log out.
   * 2. Unsubscribe web push notifications.
   * 3. Perform API logout.
   *
   * @example
   *
   * ```html
   * <button data-auth="logout">Logout</button>
   * ```
   *
   * @param {Element} [target]
   *
   * @see Terms: {@link USER}, {@link LOGOUT}
   */
  @click async logout(target) {
    const question = await t('userPool.askForLogout');

    const yes = async () => {
      await make(NotificationManager).unsubscribe().catch(console.error);
      await post(`${USER}/${LOGOUT}`, {}, target);
      disconnectLocation();
      location.mode = 'defer';
      location.search = LOGOUT;
      location.reload();
    };

    coreFeature.popup(null, 'confirm', (close) => ({question, yes, no: () => close()}), true);
  }

  /**
   * {@link KICK} the {@link USER}.
   *
   * 1. Assert the user to be authenticated.
   * 2. **Reload the app.**
   *
   * @returns {Promise<never>}
   *
   * @throws {never}
   */
  async kick() {
    ASSERT: if (this.guest) {
      await popup().warn('Terminating guest?!? Hasta la vista! I´ll be back.');
    }

    location.reload();
    await createPromise(noop);
    ASSERT: throw Error('Promise<never> failed');
  }
}

GlobalWatch(ctxSearchParams)(async (search) => {
  if (remove(search, LOGOUT)) {
    await popup().info(await t('userPool.logoutSuccess'));
  }
});

export {Auth};
